import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { inspectionService } from '@/services/inspection.service';
import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { FormField } from '@/components/shared/FormField';
import { toast } from '@/lib/swal';
import { getApiErrorMessage } from '@/lib/api-error';

export interface ReturnToEngineerTarget {
  id: string;
  inspectionNumber: string;
}

interface ReturnToEngineerDialogProps {
  target: ReturnToEngineerTarget | null;
  onClose: () => void;
  onReturned: () => void;
}

/**
 * Reused by OperationsReviewsTable (OPS Dashboard) and OperationsWorkQueuePage —
 * the sole Return-to-engineer UI in the codebase, kept here to avoid duplicating
 * the mandatory-reason validation and operationsReturn() call site.
 */
export function ReturnToEngineerDialog({
  target,
  onClose,
  onReturned,
}: ReturnToEngineerDialogProps) {
  const [reason, setReason] = useState('');

  const returnMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      inspectionService.operationsReturn(id, reason),
    onSuccess: () => {
      toast.success('Returned to engineer');
      setReason('');
      onReturned();
    },
    onError: (e: Error) => toast.error(getApiErrorMessage(e, 'Failed to return inspection')),
  });

  const handleClose = () => {
    if (returnMutation.isPending) return;
    setReason('');
    onClose();
  };

  return (
    <Modal
      open={target !== null}
      onClose={handleClose}
      title={`Return ${target?.inspectionNumber ?? ''} to Engineer`}
    >
      <div className="space-y-4">
        <FormField label="Reason for return" htmlFor="return-to-engineer-reason" required>
          <Textarea
            id="return-to-engineer-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain what needs to be corrected…"
            rows={4}
            maxLength={2000}
            disabled={returnMutation.isPending}
          />
        </FormField>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={handleClose} disabled={returnMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => target && returnMutation.mutate({ id: target.id, reason })}
            disabled={!reason.trim() || returnMutation.isPending}
          >
            {returnMutation.isPending && (
              <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
            )}
            Return
          </Button>
        </div>
      </div>
    </Modal>
  );
}
